/**
 * What this device remembers about the home-screen card: how many separate
 * visits it has seen, and whether the person is finished with it (dismissed it,
 * or installed the app).
 *
 * Per device, in `localStorage`. Every access is guarded: the accessor itself
 * throws in a private window or with site data blocked, and the card must then
 * simply not appear, never break the home screen.
 */

export const VISITS_KEY = 'ia.install.visits'
export const DONE_KEY = 'ia.install.done'
export const COUNTED_KEY = 'ia.install.counted'

/** The slice of the Storage interface used here, so tests can pass a plain object. */
export interface StorageLike {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

export function safeLocalStorage(): StorageLike | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage
  } catch {
    return null
  }
}

export function safeSessionStorage(): StorageLike | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage
  } catch {
    return null
  }
}

function read(storage: StorageLike | null, key: string): string | null {
  try {
    return storage ? storage.getItem(key) : null
  } catch {
    return null
  }
}

function write(storage: StorageLike | null, key: string, value: string): boolean {
  try {
    if (!storage) return false
    storage.setItem(key, value)
    return true
  } catch {
    // Full or blocked storage: the card just won't be remembered.
    return false
  }
}

export function readVisits(local: StorageLike | null): number {
  const n = Number.parseInt(read(local, VISITS_KEY) ?? '', 10)
  return Number.isFinite(n) && n > 0 ? n : 0
}

/**
 * Counts this visit once per browser session (reloads and page changes inside
 * the app are the same visit) and returns the total so far. With storage
 * unavailable it returns 0, which keeps the card hidden.
 */
export function recordVisit(local: StorageLike | null, session: StorageLike | null): number {
  const visits = readVisits(local)
  if (!local) return 0
  if (read(session, COUNTED_KEY) === '1') return visits
  write(session, COUNTED_KEY, '1')
  const next = visits + 1
  // A count that cannot be kept would read as the first visit forever.
  return write(local, VISITS_KEY, String(next)) ? next : 0
}

export function isInstallCardDone(local: StorageLike | null): boolean {
  return read(local, DONE_KEY) === '1'
}

/** Dismissed or installed: either way, never ask again on this device. */
export function markInstallCardDone(local: StorageLike | null): void {
  write(local, DONE_KEY, '1')
}

export interface InstallCardInput {
  isPhone: boolean
  isStandalone: boolean
  /** Visits to the signed-in app on this device, this one included. */
  visits: number
  isDone: boolean
}

/**
 * A phone browser, not yet installed, past the first visit, never dismissed.
 * Not on the first visit: someone who has just arrived is still deciding
 * whether this is for them, and asking for a home-screen slot then is too soon.
 */
export function shouldShowInstallCard(input: InstallCardInput): boolean {
  return input.isPhone && !input.isStandalone && !input.isDone && input.visits >= 2
}
